import React, { useState, useEffect } from "react";
import { Table, Switch, Modal, Button, Tooltip } from "antd";
import {
  EyeOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons"; // Import the eye icon
import DeleteConfirmation from "../../components/DeleteConfirmation.tsx";
import { useDispatch } from "react-redux";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";
import userService from "../../services/user-service.ts";
import Constant from "../../global/Constant.ts";
import { userTypeKey } from "../../global/enums.ts";

const UserManagement = () => {
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isEmailModalVisible, setEmailModalVisible] = useState(false); // State for email modal
  const [selectedUserEmails, setSelectedUserEmails] = useState([]); // State to store selected user's emails
  const [selectedUser, setSelectedUser] = useState(null); // State to store the selected user for status change
  const [newStatus, setNewStatus] = useState(null); // State to store the new status

  // New states for participant status change confirmation
  const [isParticipantStatusModalVisible, setParticipantStatusModalVisible] =
    useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null); // Store the selected participant
  const [newParticipantStatus, setNewParticipantStatus] = useState(null); // Store the new status

  const dispatch = useDispatch();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchUsers(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchUsers = async (page, size) => {
    dispatch(setLoading(true));

    try {
      const response = await userService.getAllUsers(
        `${Constant.userlist}?page=${page}&perPage=${size}`
      );

      setUsers(response.data);
      setTotalUsers(response.pagination.totalUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleStatusChange = async (userId, status) => {
    setSelectedUser(userId);
    setNewStatus(status);
    setDeleteModalVisible(true); // Show the confirmation modal
  };

  const handleStatusChangeConfirm = async () => {
    setDeleteModalVisible(false); // Close the confirmation modal
    dispatch(setLoading(true));
    try {
      const payload = {
        userId: selectedUser?.UserId,
        status: newStatus ? 1 : 0,
      };

      await userService.updateUserDetailsById(Constant.updateuser, payload);

      setUsers((prevUsers: any) =>
        prevUsers.map((user) =>
          user.UserID === selectedUser?.UserId
            ? { ...user, Status: newStatus ? 1 : 0 }
            : user
        )
      );

      await fetchUsers(currentPage, pageSize);
      dispatch(
        addNotification({
          title: "Success",
          description: "User status updated successfully",
          type: "success",
        })
      );
    } catch (error) {
      console.error("Failed to update user status:", error);
      dispatch(
        addNotification({
          title: "Error",
          description: "Failed to update user status",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Function to handle eye button click
  const handleViewEmails = (user) => {
    setSelectedUserEmails(user.participantDetails || []); // Set the selected user's emails
    setEmailModalVisible(true); // Open the modal
  };

  const handleParticipantStatusChange = (participant, status) => {
    setSelectedParticipant(participant); // Store the selected participant
    setNewParticipantStatus(status); // Store the new status
    setParticipantStatusModalVisible(true); // Show the confirmation modal
  };

  const handleParticipantStatusChangeConfirm = async () => {
    setParticipantStatusModalVisible(false); // Close the confirmation modal
    dispatch(setLoading(true));

    try {
      const payload = {
        userId: selectedParticipant.UserId, // Use the selected participant's ID
        status: newParticipantStatus ? 1 : 0,
      };

      // Call the API to update the participant status
      await userService.updateUserDetailsById(Constant.updateuser, payload);

      // Update the local state to reflect the status change for the specific participant
      setSelectedUserEmails((prevEmails: any) =>
        prevEmails.map((email) =>
          email.UserId === selectedParticipant.UserId
            ? { ...email, Status: newParticipantStatus ? 1 : 0 } // Update the status
            : email
        )
      );

      // Refresh the user list
      await fetchUsers(currentPage, pageSize);

      dispatch(
        addNotification({
          title: "Success",
          description: "Participant status updated successfully",
          type: "success",
        })
      );
    } catch (error) {
      console.error("Failed to update participant status:", error);

      dispatch(
        addNotification({
          title: "Error",
          description: "Failed to update participant status",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Columns for the main table
  const columns = [
    {
      title: "Sl.No", // Column title
      key: "slNo",
      width: 80,
      align: "left",
      className: "vm__no-wrap",
      render: (_, record, index) => {
        // Calculate the serial number based on the current page and page size
        return (currentPage - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Name",
      dataIndex: "FirstName",
      key: "FirstName",
      width: 300,
      className: "vm__no-wrap",
      render: (text, record) => `${record.FirstName}`,
    },
    {
      title: "Email",
      dataIndex: "Email",
      key: "Email",
      className: "vm__no-wrap",
    },
    {
      title: "User Type",
      dataIndex: "UserType",
      key: "UserType",
      className: "vm__if-type vm__no-wrap",
      width: 100,
      render: (UserType) => {
        if (userTypeKey[UserType] === "Group") {
          return (
            <Tooltip title={userTypeKey[UserType]}>
              <span className="vm__table-icons">
                <UsergroupAddOutlined />
              </span>
            </Tooltip>
          );
        } else if (userTypeKey[UserType] === "Individual") {
          return (
            <Tooltip title={userTypeKey[UserType]}>
              <span className="vm__table-icons">
                <UserOutlined />
              </span>
            </Tooltip>
          );
        } else {
          return null; // Handle unknown types
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      className: "vm__no-wrap",
      render: (_, record) => (
        <>
          <div className="vm__d-inline-block vm__valign-sup">
            <Switch
              size="small"
              checked={record.Status === 1}
              onChange={(checked) => handleStatusChange(record, checked)}
            />
          </div>

          {userTypeKey[record.UserType] === "Group" && ( // Show eye button only for groups
            <div className="vm__d-inline-block vm__valign-base vm__ms-3">
              {record?.participantDetails?.length > 0 ? (
                <Tooltip title={"View Participants"}>
                  <Button
                    type="text"
                    className="vm__table-icons vm__me-0"
                    icon={<EyeOutlined style={{ fontSize: "24px" }} />}
                    onClick={() => handleViewEmails(record)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title={"No participants"}>
                  <Button
                    type="text"
                    className="vm__table-icons vm__me-0"
                    icon={
                      <EyeOutlined style={{ fontSize: "24px", opacity: 0.5 }} />
                    }
                    disabled
                    onClick={() => handleViewEmails(record)}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </>
      ),
    },
  ];

  // Columns for the email modal table
  const emailColumns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      className: "vm__no-wrap",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      className: "vm__no-wrap",
    },
    {
      title: "Status",
      key: "status",
      className: "vm__no-wrap",
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.Status === 1} // Ensure this matches your data structure
          onChange={(checked) => handleParticipantStatusChange(record, checked)}
        />
      ),
    },
  ];

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: totalUsers,
    showSizeChanger: true,
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    onShowSizeChange: (current, size) => {
      setPageSize(size);
    },
  };

  return (
    <div className="vm-user-managemnt vm__container vm__h-100">
      <div className="vm__d-flex vm__flex-column vm__h-100">
        <div className="vm__flex-auto">
          <div className="vm__d-flex vm__align-items-center vm__mb-3">
            <div className="vm__flex-fill">
              <h3 className="vm__page-header">Users</h3>
            </div>
          </div>
        </div>
        <div className="vm__flex-fill vm__overflow-auto">
          <div className="vm__custom-table">
            <Table
              dataSource={users}
              columns={columns}
              rowKey="UserID"
              className="vm__table-container"
              scroll={{ x: true }}
              pagination={paginationConfig}
            />
          </div>
        </div>
      </div>

      {/* Email Modal */}
      <Modal
        title="Participant Emails"
        visible={isEmailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        footer={null}
        maskClosable={false}
        centered
        width={700}
      >
        <Table
          dataSource={selectedUserEmails} // Map emails to table data
          columns={emailColumns}
          rowKey="email"
          pagination={false}
          className="vm__table-scroll"
          locale={{ emptyText: "No participants" }} // Custom no-data text
        />
      </Modal>

      {/* Confirmation Modal for User Status Change */}
      <DeleteConfirmation
        title={"Confirm Status"}
        isVisible={isDeleteModalVisible}
        onConfirm={handleStatusChangeConfirm}
        onCancel={handleDeleteCancel}
        okText={"Yes"}
        content={"Are you sure you want to change the status?"}
      />

      {/* Confirmation Modal for Participant Status Change */}
      <DeleteConfirmation
        title={"Confirm Participant Status"}
        isVisible={isParticipantStatusModalVisible}
        onConfirm={handleParticipantStatusChangeConfirm}
        onCancel={() => setParticipantStatusModalVisible(false)}
        okText={"Yes"}
        content={"Are you sure you want to change the participant's status?"}
      />
    </div>
  );
};

export default UserManagement;
